import * as XLSX from 'xlsx'

export type CompreAgoraFileRole = 'estoque' | 'entradas' | 'otif'

export interface CompreAgoraSourceFile {
  name: string
  data: ArrayBuffer | Uint8Array
}

export interface CompreAgoraParserOptions {
  leadTimeDays: number
  targetCoverageDays: number
  periodDaysOverride?: number
  daysConsideredByMonth?: Record<string, number>
  storeCnpjByKey?: Record<string, string>
  allowedProducts?: CompreAgoraAllowedProduct[]
  excludedProducts?: CompreAgoraProductScopeExclusion[]
  existingSuppliers?: CompreAgoraExistingSupplier[]
}

export interface CompreAgoraAllowedProduct {
  ean: string
  cnpj?: string
  nomeProduto?: string
  descricaoProduto?: string
  nomeIndustria: 'Unilever' | 'Kenvue' | string
  foto?: string
}
export interface CompreAgoraProductScopeExclusion {
  ean: string
  cnpj?: string
}


export type CompreAgoraLeadTimeSource = 'otif' | 'global' | 'manual'

export interface CompreAgoraExistingSupplier {
  fornecedor: string
  lead_time: number | string
  lead_time_source?: CompreAgoraLeadTimeSource
}

export interface CompreAgoraSupplierImport {
  fornecedor: string
  leadTimeDays: number
  leadTimeSource: CompreAgoraLeadTimeSource
}

export type CompreAgoraClassification =
  | 'Ruptura instalada'
  | 'Sem venda (avaliar)'
  | 'Ruptura iminente'
  | 'Perto do mínimo'
  | 'Cobertura crítica'
  | 'Excesso'
  | 'Saudável'

export interface CompreAgoraAnalyticRow {
  anoMes: string
  cnpjLoja: string
  gtin: string
  ean: string
  produto: string
  foto?: string
  fornecedor: string
  vendaQtdMes: number
  vendaRs: number
  comprasQtd: number
  comprasRs: number
  estoqueAtualQtd: number
  precoMedioRs: number
  diasDoMes: number
  cmd: number
  coberturaAtualDias: number
  ltDias: number
  flagLtDefault: number
  coberturaAlvoDias: number
  flagCoberturaDefault: number
  ss: number
  ddlt: number
  rop: number
  estoqueAlvo: number
  pedidoSugerido: number
  flagSemVenda: number
  qtdAjustadaUsuario: number | null
  pedidoFinalQtd: number
  origemPedido: 'Modelo' | 'Usuário'
  flagAjustado: number
  classificacao: CompreAgoraClassification
  chave: string
}

export interface CompreAgoraImportRow {
  ean: string
  produto: string
  foto?: string
  cnpjLoja: string
  fornecedor: string
  saldoInicial: number
  entrada: number
  saida: number
  periodDays: number
  referenceDate: string
  saldoFinal: number
  estoqueAtual: number
}

export interface CompreAgoraRoleSummary {
  role: CompreAgoraFileRole
  fileNames: string[]
  rowCount: number
  eligibleRowCount: number
  validRowCount: number
  rejectedRowCount: number
}

export interface CompreAgoraBundleResult {
  matched: boolean
  errors: string[]
  analyticRows: CompreAgoraAnalyticRow[]
  importRows: CompreAgoraImportRow[]
  suppliers: CompreAgoraSupplierImport[]
  roleSummaries: CompreAgoraRoleSummary[]
  unknownFiles: string[]
  allowedProductCount: number
}

type SheetRow = unknown[]

interface DetectedSource {
  fileIndex: number
  fileName: string
  role: CompreAgoraFileRole
  sheetName: string
  headerRowIndex: number
  columns: Record<string, number>
  headers: Record<string, string>
}

interface StockAggregate {
  anoMes: string
  cnpjLoja: string
  gtin: string
  produto: string
  foto: string
  entradaQtdMes: number
  vendaQtdMes: number
  estoqueAtualQtd: number
  saldoInicial: number
  saldoFinal: number
  firstSnapshotAt: number
  lastSnapshotAt: number
}

interface EntryAggregate {
  comprasQtd: number
  comprasRs: number
}

interface OtifAggregate {
  leadTimeWeightedTotal: number
  weightTotal: number
}

interface SupplierCandidate {
  fornecedor: string
  timestamp: number
  quantity: number
  sourcePriority: number
}

const REQUIRED_HEADERS: Record<CompreAgoraFileRole, string[]> = {
  estoque: [
    'data_referencia',
    'cnpj_loja',
    'ean',
    'descricao_produto',
    'saldo_inicial',
    'entrada',
    'saida',
    'saldo_final',
    'estoque_atual',
  ],
  entradas: [
    'data_movimento',
    'cnpj_loja',
    'ean',
    'quantidade_entrada',
    'custo_total',
  ],
  otif: [
    'data_pedido',
    'cnpj_loja',
    'ean',
    'quantidade_entregue',
  ],
}

const HEADER_ALIASES: Record<CompreAgoraFileRole, Record<string, string[]>> = {
  estoque: {
    data_referencia: ['data_referencia'],
    cnpj_loja: ['cnpj_loja'],
    ean: ['ean'],
    descricao_produto: ['descricao_produto'],
    saldo_inicial: ['saldo_inicial'],
    entrada: ['entrada'],
    saida: ['saida'],
    saldo_final: ['saldo_final'],
    estoque_atual: ['estoque_atual'],
  },
  entradas: {
    data_movimento: ['data_movimento', 'data_recebimento'],
    cnpj_loja: ['cnpj_loja', 'loja_key'],
    ean: ['ean', 'gtin_principal'],
    descricao_produto: ['descricao_produto', 'desc_produto'],
    fornecedor: ['fornecedor'],
    quantidade_entrada: ['quantidade_entrada', 'quantidade_recebida'],
    custo_total: ['custo_total', 'ni_valor_bruto'],
  },
  otif: {
    data_pedido: ['data_pedido'],
    data_prevista_entrega: ['data_prevista_entrega'],
    data_entrega: ['data_entrega'],
    cnpj_loja: ['cnpj_loja'],
    ean: ['ean'],
    quantidade_entregue: ['quantidade_entregue'],
    fornecedor: ['fornecedor'],
  },
}

const ROLE_LABELS: Record<CompreAgoraFileRole, string> = {
  estoque: 'Estoque (Anexo 1)',
  entradas: 'Entradas (Anexo 2)',
  otif: 'OTIF (Anexo 3)',
}

export const DEFAULT_STORE_CNPJ_BY_KEY: Record<string, string> = {
  '1': '993000100',
  '2': '993000444',
  '3': '993000363',
  '5': '993000606',
  '7': '993000797',
  '8': '993000878',
  '9': '993000959',
  '12': '993001092',
}

const KEY_SEPARATOR = '\u001f'

function getCellText(value: unknown): string {
  if (value instanceof Date) return value.toISOString()
  if (typeof value === 'number' && Number.isFinite(value)) return String(value)
  return String(value ?? '')
    .replace(/\s+/g, ' ')
    .trim()
}

export function normalizeCompreAgoraHeader(value: unknown): string {
  return getCellText(value)
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, '_')
    .trim()
}

function buildColumnMap(
  header: SheetRow,
  role: CompreAgoraFileRole
): {columns: Record<string, number>; headers: Record<string, string>} {
  const rawColumns = header.reduce<Record<string, number>>((map, value, index) => {
    const normalized = normalizeCompreAgoraHeader(value)
    if (normalized) map[normalized] = index
    return map
  }, {})
  const columns: Record<string, number> = {}
  const headers: Record<string, string> = {}

  Object.entries(HEADER_ALIASES[role]).forEach(([canonical, aliases]) => {
    const matchedAlias = aliases.find((alias) => rawColumns[alias] !== undefined)
    if (matchedAlias) {
      columns[canonical] = rawColumns[matchedAlias]
      headers[canonical] = matchedAlias
    }
  })

  return {columns, headers}
}

function findHeaderRole(
  row: SheetRow
): {role: CompreAgoraFileRole; columns: Record<string, number>; headers: Record<string, string>} | null {
  const matches = (Object.keys(REQUIRED_HEADERS) as CompreAgoraFileRole[])
    .map((role) => ({role, ...buildColumnMap(row, role)}))
    .filter(({role, columns}) =>
      REQUIRED_HEADERS[role].every((header) => columns[header] !== undefined)
    )

  return matches.length === 1 ? matches[0] : null
}

function readDetectedSources(files: CompreAgoraSourceFile[]): {
  detected: DetectedSource[]
  unknownFiles: string[]
  readErrors: string[]
} {
  const detected: DetectedSource[] = []
  const unknownFiles: string[] = []
  const readErrors: string[] = []

  for (let fileIndex = 0; fileIndex < files.length; fileIndex++) {
    const file = files[fileIndex]
    try {
      const workbook = XLSX.read(file.data, {type: 'array', cellDates: true, sheetRows: 10})
      const matches: DetectedSource[] = []

      for (const sheetName of workbook.SheetNames) {
        const worksheet = workbook.Sheets[sheetName]
        const rows = XLSX.utils.sheet_to_json<SheetRow>(worksheet, {
          header: 1,
          defval: '',
          raw: true,
        })

        for (let rowIndex = 0; rowIndex < Math.min(rows.length, 10); rowIndex++) {
          const headerMatch = findHeaderRole(rows[rowIndex])
          if (!headerMatch) continue
          matches.push({
            fileIndex,
            fileName: file.name,
            role: headerMatch.role,
            sheetName,
            headerRowIndex: rowIndex,
            columns: headerMatch.columns,
            headers: headerMatch.headers,
          })
          break
        }
      }

      const distinctRoles = Array.from(new Set(matches.map((match) => match.role)))
      if (distinctRoles.length === 0) {
        unknownFiles.push(file.name)
      } else if (distinctRoles.length > 1) {
        readErrors.push(
          `${file.name}: o arquivo contém mais de um layout CompreAgora. Envie Estoque, Entradas e OTIF em arquivos separados.`
        )
      } else {
        detected.push(matches.find((match) => match.role === distinctRoles[0]) as DetectedSource)
      }
    } catch {
      unknownFiles.push(file.name)
    }
  }

  return {detected, unknownFiles, readErrors}
}

export function detectCompreAgoraBundle(files: CompreAgoraSourceFile[]): {
  matched: boolean
  roles: CompreAgoraFileRole[]
  unknownFiles: string[]
} {
  const {detected, unknownFiles, readErrors} = readDetectedSources(files)
  return {
    matched: detected.length > 0 || readErrors.length > 0,
    roles: Array.from(new Set(detected.map((source) => source.role))),
    unknownFiles,
  }
}

function parseNumber(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null
  let text = getCellText(value).replace(/[^\d,.-]/g, '')
  if (!text || !/\d/.test(text)) return null

  const lastComma = text.lastIndexOf(',')
  const lastDot = text.lastIndexOf('.')
  if (lastComma !== -1 && lastDot !== -1) {
    text = lastComma > lastDot ? text.replace(/\./g, '').replace(',', '.') : text.replace(/,/g, '')
  } else if (lastComma !== -1) {
    text = text.replace(',', '.')
  }

  const parsed = Number(text)
  return Number.isFinite(parsed) ? parsed : null
}

function parseIdentifier(value: unknown): string {
  const text = getCellText(value)
  return text.endsWith('.0') ? text.slice(0, -2) : text
}

function normalizeDigitsIdentifier(value: unknown): string {
  return parseIdentifier(value).replace(/\D/g, '')
}

function parseDate(value: unknown): Date | null {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return new Date(Date.UTC(value.getUTCFullYear(), value.getUTCMonth(), value.getUTCDate()))
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    const parsed = XLSX.SSF.parse_date_code(value)
    return parsed ? new Date(Date.UTC(parsed.y, parsed.m - 1, parsed.d)) : null
  }

  const text = getCellText(value)
  const brDate = text.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/)
  if (brDate) {
    return new Date(Date.UTC(Number(brDate[3]), Number(brDate[2]) - 1, Number(brDate[1])))
  }
  const isoDate = text.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/)
  if (isoDate) {
    return new Date(Date.UTC(Number(isoDate[1]), Number(isoDate[2]) - 1, Number(isoDate[3])))
  }
  return null
}

function getLeadTimeDays(orderDateValue: unknown, deliveryDateValue: unknown): number | null {
  const orderDate = parseDate(orderDateValue)
  const deliveryDate = parseDate(deliveryDateValue)
  if (!orderDate || !deliveryDate) return null
  const difference = Math.round((deliveryDate.getTime() - orderDate.getTime()) / 86_400_000)
  return difference >= 0 ? difference : null
}

function getNumberOrZero(value: unknown): number {
  return parseNumber(value) ?? 0
}

function getNonNegativeNumber(value: unknown): number {
  return Math.max(0, getNumberOrZero(value))
}

function toYearMonth(year: number, month: number): string | null {
  if (
    !Number.isInteger(year) ||
    !Number.isInteger(month) ||
    year < 1900 ||
    month < 1 ||
    month > 12
  ) {
    return null
  }
  return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}`
}

function parseYearMonth(value: unknown): string | null {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return toYearMonth(value.getUTCFullYear(), value.getUTCMonth() + 1)
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    const parsed = XLSX.SSF.parse_date_code(value)
    return parsed ? toYearMonth(parsed.y, parsed.m) : null
  }

  const text = getCellText(value)
  const brDate = text.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/)
  if (brDate) return toYearMonth(Number(brDate[3]), Number(brDate[2]))
  const isoDate = text.match(/^(\d{4})-(\d{1,2})(?:-(\d{1,2}))?/)
  if (isoDate) return toYearMonth(Number(isoDate[1]), Number(isoDate[2]))
  return null
}

function getCalendarDays(yearMonth: string): number {
  const [year, month] = yearMonth.split('-').map(Number)
  return new Date(Date.UTC(year, month, 0)).getUTCDate()
}


function makeJoinKey(yearMonth: string, store: string, gtin: string): string {
  return [yearMonth, store, gtin].join(KEY_SEPARATOR)
}

function makeStockKey(yearMonth: string, store: string, gtin: string): string {
  return [yearMonth, store, gtin].join(KEY_SEPARATOR)
}

function makeDisplayKey(yearMonth: string, store: string, gtin: string): string {
  return `${yearMonth.replace('-', '')}|${store}|${gtin}`
}

function normalizeSupplierKey(value: unknown): string {
  return getCellText(value)
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

function makeSupplierKey(supplier: string): string {
  return normalizeSupplierKey(supplier)
}

function makeProductSupplierKey(store: string, gtin: string): string {
  return [store, gtin].join(KEY_SEPARATOR)
}

function getTimestamp(value: unknown): number {
  return parseDate(value)?.getTime() ?? 0
}

function roundLikeExcel(value: number, digits: number): number {
  if (!Number.isFinite(value)) return 0
  const factor = 10 ** digits
  return (Math.sign(value) * Math.round((Math.abs(value) + Number.EPSILON) * factor)) / factor
}

function compareText(a: string, b: string): number {
  if (a < b) return -1
  if (a > b) return 1
  return 0
}

function classifyRow(
  stock: number,
  flagNoSales: number,
  safetyStock: number,
  reorderPoint: number,
  currentCoverage: number,
  leadTime: number,
  targetCoverage: number
): CompreAgoraClassification {
  if (stock === 0) return 'Ruptura instalada'
  if (flagNoSales === 1) return 'Sem venda (avaliar)'
  if (stock < safetyStock) return 'Ruptura iminente'
  if (stock <= reorderPoint) return 'Perto do mínimo'
  if (currentCoverage < leadTime) return 'Cobertura crítica'
  if (currentCoverage > targetCoverage) return 'Excesso'
  return 'Saudável'
}

type SourceRowGetter = (column: string) => unknown

function forEachSourceRow(
  file: CompreAgoraSourceFile,
  source: DetectedSource,
  callback: (getValue: SourceRowGetter, rowNumber: number) => void
): number {
  const workbook = XLSX.read(file.data, {type: 'array', cellDates: true})
  const worksheet = workbook.Sheets[source.sheetName]
  const reference = worksheet?.['!ref']
  if (!worksheet || !reference) return 0

  const range = XLSX.utils.decode_range(reference)
  const columnIndexes = Array.from(new Set(Object.values(source.columns)))
  let rowCount = 0

  for (let rowIndex = source.headerRowIndex + 1; rowIndex <= range.e.r; rowIndex++) {
    const valuesByColumn = new Map<number, unknown>()
    for (const columnIndex of columnIndexes) {
      const cell = worksheet[XLSX.utils.encode_cell({r: rowIndex, c: columnIndex})]
      valuesByColumn.set(columnIndex, cell?.v ?? '')
    }
    if (!columnIndexes.some((columnIndex) => getCellText(valuesByColumn.get(columnIndex)) !== '')) {
      continue
    }

    rowCount++
    callback(
      (column) => {
        const columnIndex = source.columns[column]
        return columnIndex === undefined ? '' : valuesByColumn.get(columnIndex)
      },
      rowIndex + 1
    )
  }

  return rowCount
}

export function parseCompreAgoraBundle(
  files: CompreAgoraSourceFile[],
  options: CompreAgoraParserOptions
): CompreAgoraBundleResult {
  const {detected, unknownFiles, readErrors} = readDetectedSources(files)
  if (detected.length === 0 && readErrors.length === 0) {
    return {
      matched: false,
      errors: [],
      analyticRows: [],
      importRows: [],
      suppliers: [],
      roleSummaries: [],
      unknownFiles,
      allowedProductCount: 0,
    }
  }

  const errors = [...readErrors]
  const sourcesByRole = (Object.keys(REQUIRED_HEADERS) as CompreAgoraFileRole[]).reduce<
    Record<CompreAgoraFileRole, DetectedSource[]>
  >(
    (result, role) => {
      result[role] = detected.filter((source) => source.role === role)
      return result
    },
    {estoque: [], entradas: [], otif: []}
  )

  for (const role of Object.keys(sourcesByRole) as CompreAgoraFileRole[]) {
    if (sourcesByRole[role].length === 0)
      errors.push(`Arquivo obrigatório ausente: ${ROLE_LABELS[role]}.`)
  }
  if (unknownFiles.length > 0) {
    errors.push(
      `Arquivos não reconhecidos no pacote CompreAgora: ${unknownFiles.join(
        ', '
      )}. Selecione somente Estoque, Entradas e OTIF.`
    )
  }
  if (!Number.isFinite(options.leadTimeDays) || options.leadTimeDays <= 0) {
    errors.push('O parâmetro de lead time deve ser maior que zero.')
  }
  if (!Number.isFinite(options.targetCoverageDays) || options.targetCoverageDays <= 0) {
    errors.push('O parâmetro de cobertura alvo deve ser maior que zero.')
  }

  const allowedProductByEan = new Map<string, CompreAgoraAllowedProduct>()
  const allowedProductByScope = new Map<string, CompreAgoraAllowedProduct>()
  for (const product of options.allowedProducts ?? []) {
    const ean = normalizeDigitsIdentifier(product.ean)
    const cnpj = normalizeDigitsIdentifier(product.cnpj)
    const industry = normalizeCompreAgoraHeader(product.nomeIndustria)
    if (!ean || (industry !== 'unilever' && industry !== 'kenvue')) continue
    const normalizedProduct = {...product, ean, cnpj: cnpj || undefined}
    if (cnpj) allowedProductByScope.set(makeProductSupplierKey(cnpj, ean), normalizedProduct)
    else if (!allowedProductByEan.has(ean)) allowedProductByEan.set(ean, normalizedProduct)
  }
  const excludedEans = new Set<string>()
  const excludedProductScopes = new Set<string>()
  for (const product of options.excludedProducts ?? []) {
    const ean = normalizeDigitsIdentifier(product.ean)
    const cnpj = normalizeDigitsIdentifier(product.cnpj)
    if (!ean) continue
    if (cnpj) excludedProductScopes.add(makeProductSupplierKey(cnpj, ean))
    else excludedEans.add(ean)
  }
  const resolveAllowedProduct = (cnpj: string, ean: string): CompreAgoraAllowedProduct | undefined => {
    if (excludedEans.has(ean) || excludedProductScopes.has(makeProductSupplierKey(cnpj, ean))) {
      return undefined
    }
    return allowedProductByScope.get(makeProductSupplierKey(cnpj, ean)) ?? allowedProductByEan.get(ean)
  }
  const allowedProductCount = new Set([
    ...Array.from(allowedProductByEan.keys()),
    ...Array.from(allowedProductByScope.values(), (product) => product.ean),
  ]).size
  if (allowedProductCount === 0) {
    errors.push(
      'Não foi possível carregar a lista de EANs ativos das indústrias Unilever e Kenvue.'
    )
  }

  const roleSummaries: CompreAgoraRoleSummary[] = (
    Object.keys(sourcesByRole) as CompreAgoraFileRole[]
  ).map((role) => ({
    role,
    fileNames: sourcesByRole[role].map((source) => source.fileName),
    rowCount: 0,
    eligibleRowCount: 0,
    validRowCount: 0,
    rejectedRowCount: 0,
  }))

  const roleSummaryByRole = new Map(roleSummaries.map((summary) => [summary.role, summary]))
  if (errors.length > 0) {
    return {
      matched: true, errors, analyticRows: [], importRows: [], suppliers: [], roleSummaries, unknownFiles,
      allowedProductCount,
    }
  }

  const storeMap = options.storeCnpjByKey ?? DEFAULT_STORE_CNPJ_BY_KEY
  const existingSupplierByKey = new Map<string, {leadTimeDays: number; leadTimeSource: CompreAgoraLeadTimeSource}>()
  for (const supplier of options.existingSuppliers ?? []) {
    const fornecedor = getCellText(supplier.fornecedor)
    const leadTimeDays = Number(supplier.lead_time)
    const leadTimeSource = ['otif', 'global', 'manual'].includes(supplier.lead_time_source ?? '')
      ? (supplier.lead_time_source as CompreAgoraLeadTimeSource)
      : 'otif'
    if (!fornecedor || !Number.isFinite(leadTimeDays) || leadTimeDays < 0 || leadTimeDays > 365)
      continue
    existingSupplierByKey.set(makeSupplierKey(fornecedor), {leadTimeDays, leadTimeSource})
  }
  const stockAggregates = new Map<string, StockAggregate>()
  const entryAggregates = new Map<string, EntryAggregate>()
  const supplierOtifAggregates = new Map<string, OtifAggregate>()
  const supplierObservations = new Map<string, {fornecedor: string}>()
  const productSupplierCandidates = new Map<string, SupplierCandidate>()
  let rowErrorCount = 0
  const addRowError = (message: string) => {
    rowErrorCount++
    if (rowErrorCount <= 50) errors.push(message)
  }

  const registerSupplierObservation = (supplierValue: unknown): string => {
    const fornecedor = getCellText(supplierValue)
    if (!fornecedor) return ''
    const supplierKey = makeSupplierKey(fornecedor)
    if (!supplierObservations.has(supplierKey))
      supplierObservations.set(supplierKey, {fornecedor})
    return fornecedor
  }

  const registerSupplierCandidate = (
    cnpjLoja: string,
    gtin: string,
    supplierValue: unknown,
    dateValue: unknown,
    quantity: number,
    sourcePriority: number
  ) => {
    const fornecedor = registerSupplierObservation(supplierValue)
    if (!fornecedor) return

    const productKey = makeProductSupplierKey(cnpjLoja, gtin)
    const timestamp = getTimestamp(dateValue)
    const candidate = {fornecedor, timestamp, quantity, sourcePriority}
    const current = productSupplierCandidates.get(productKey)
    const shouldReplace =
      !current ||
      timestamp > current.timestamp ||
      (timestamp === current.timestamp && sourcePriority > current.sourcePriority) ||
      (timestamp === current.timestamp &&
        sourcePriority === current.sourcePriority &&
        quantity > current.quantity)

    if (shouldReplace) productSupplierCandidates.set(productKey, candidate)
  }

  for (const source of sourcesByRole.estoque) {
    const summary = roleSummaryByRole.get('estoque') as CompreAgoraRoleSummary
    const rowCount = forEachSourceRow(files[source.fileIndex], source, (getValue, rowNumber) => {
      const gtin = normalizeDigitsIdentifier(getValue('ean'))
      const cnpjLoja = normalizeDigitsIdentifier(getValue('cnpj_loja'))
      const allowedProduct = resolveAllowedProduct(cnpjLoja, gtin)
      if (!allowedProduct) return
      summary.eligibleRowCount++

      const referenceDate = parseDate(getValue('data_referencia'))
      const anoMes = referenceDate ? parseYearMonth(referenceDate) : null
      const sourceProduct = getCellText(getValue('descricao_produto'))
      const produto =
        getCellText(allowedProduct.nomeProduto) ||
        getCellText(allowedProduct.descricaoProduto) ||
        sourceProduct
      if (!referenceDate || !anoMes || !cnpjLoja || !gtin || !produto) {
        summary.rejectedRowCount++
        addRowError(
          `${source.fileName}, linha ${rowNumber}: data de referência, loja, EAN ou produto inválido.`
        )
        return
      }
      summary.validRowCount++

      const key = makeStockKey(anoMes, cnpjLoja, gtin)
      const snapshotAt = referenceDate.getTime()
      const estoqueAtual = getNumberOrZero(getValue('estoque_atual'))
      const entradaQtdMes = getNonNegativeNumber(getValue('entrada'))
      const vendaQtdMes = getNonNegativeNumber(getValue('saida'))
      const saldoInicial = getNumberOrZero(getValue('saldo_inicial'))
      const saldoFinal = getNumberOrZero(getValue('saldo_final'))
      const current = stockAggregates.get(key)
      if (current) {
        current.vendaQtdMes += vendaQtdMes
        current.entradaQtdMes += entradaQtdMes
        if (snapshotAt < current.firstSnapshotAt) {
          current.firstSnapshotAt = snapshotAt
          current.saldoInicial = saldoInicial
        }
        if (snapshotAt >= current.lastSnapshotAt) {
          current.lastSnapshotAt = snapshotAt
          current.estoqueAtualQtd = estoqueAtual
          current.saldoFinal = saldoFinal
        }
      } else {
        stockAggregates.set(key, {
          anoMes,
          cnpjLoja,
          gtin,
          produto,
          foto: getCellText(allowedProduct.foto),
          entradaQtdMes,
          vendaQtdMes,
          estoqueAtualQtd: estoqueAtual,
          saldoInicial,
          saldoFinal,
          firstSnapshotAt: snapshotAt,
          lastSnapshotAt: snapshotAt,
        })
      }
    })
    summary.rowCount += rowCount
  }

  for (const source of sourcesByRole.entradas) {
    const summary = roleSummaryByRole.get('entradas') as CompreAgoraRoleSummary
    const rowCount = forEachSourceRow(files[source.fileIndex], source, (getValue, rowNumber) => {
      const gtin = normalizeDigitsIdentifier(getValue('ean'))

      const anoMes = parseYearMonth(getValue('data_movimento'))
      const storeIdentifier = normalizeDigitsIdentifier(getValue('cnpj_loja'))
      const cnpjLoja =
        source.headers.cnpj_loja === 'loja_key'
          ? storeMap[storeIdentifier]
          : storeIdentifier
      if (!cnpjLoja) {
        summary.rejectedRowCount++
        addRowError(`${source.fileName}, linha ${rowNumber}: loja "${storeIdentifier}" sem CNPJ válido.`)
        return
      }
      if (!resolveAllowedProduct(cnpjLoja, gtin)) return
      summary.eligibleRowCount++
      if (!anoMes || !gtin) {
        summary.rejectedRowCount++
        addRowError(`${source.fileName}, linha ${rowNumber}: data de movimento ou EAN inválido.`)
        return
      }
      summary.validRowCount++

      const key = makeJoinKey(anoMes, cnpjLoja, gtin)
      const current = entryAggregates.get(key) ?? {comprasQtd: 0, comprasRs: 0}
      const quantity = getNonNegativeNumber(getValue('quantidade_entrada'))
      current.comprasQtd += quantity
      current.comprasRs += getNonNegativeNumber(getValue('custo_total'))
      entryAggregates.set(key, current)
      registerSupplierCandidate(
        cnpjLoja,
        gtin,
        getValue('fornecedor'),
        getValue('data_movimento'),
        quantity,
        2
      )
    })
    summary.rowCount += rowCount
  }

  for (const source of sourcesByRole.otif) {
    const summary = roleSummaryByRole.get('otif') as CompreAgoraRoleSummary
    const rowCount = forEachSourceRow(files[source.fileIndex], source, (getValue) => {
      const gtin = normalizeDigitsIdentifier(getValue('ean'))
      const anoMes = parseYearMonth(getValue('data_pedido'))
      const cnpjLoja = normalizeDigitsIdentifier(getValue('cnpj_loja'))
      const catalogMatched = Boolean(resolveAllowedProduct(cnpjLoja, gtin))
      if (catalogMatched) summary.eligibleRowCount++

      const quantity = getNonNegativeNumber(getValue('quantidade_entregue'))
      const fornecedor = getCellText(getValue('fornecedor'))
      if (!anoMes || !cnpjLoja || !gtin || !fornecedor) {
        summary.rejectedRowCount++
        return
      }
      registerSupplierObservation(fornecedor)

      if (catalogMatched) {
        registerSupplierCandidate(
          cnpjLoja,
          gtin,
          fornecedor,
          getValue('data_pedido'),
          quantity,
          1
        )
      }

      const leadTimeDays =
        getLeadTimeDays(getValue('data_pedido'), getValue('data_entrega')) ??
        getLeadTimeDays(getValue('data_pedido'), getValue('data_prevista_entrega'))
      if (leadTimeDays === null) {
        summary.rejectedRowCount++
        return
      }
      summary.validRowCount++

      const weight = Math.max(1, quantity)
      const supplierKey = makeSupplierKey(fornecedor)
      const current = supplierOtifAggregates.get(supplierKey) ?? {
        leadTimeWeightedTotal: 0,
        weightTotal: 0,
      }
      current.leadTimeWeightedTotal += leadTimeDays * weight
      current.weightTotal += weight
      supplierOtifAggregates.set(supplierKey, current)
    })
    summary.rowCount += rowCount
  }
  const otifSummary = roleSummaryByRole.get('otif') as CompreAgoraRoleSummary
  if (otifSummary.rowCount > 0 && otifSummary.validRowCount === 0)
    errors.push(`${otifSummary.fileNames.join(", ")}: nenhuma linha com fornecedor e datas válidas encontrada no OTIF.`)

  if (stockAggregates.size === 0)
    errors.push('Nenhuma linha válida encontrada no arquivo de Estoque.')
  if (errors.length > 0) {
    if (rowErrorCount > 50) {
      errors.push(`${rowErrorCount - 50} erros adicionais de linha foram omitidos.`)
    }
    return {
      matched: true, errors, analyticRows: [], importRows: [], suppliers: [], roleSummaries, unknownFiles,
      allowedProductCount,
    }
  }

  const suppliers = Array.from(supplierObservations.values())
    .map(({fornecedor}): CompreAgoraSupplierImport => {
      const supplierKey = makeSupplierKey(fornecedor)
      const existing = existingSupplierByKey.get(supplierKey)
      if (existing?.leadTimeSource === 'manual') {
        return {
          fornecedor,
          leadTimeDays: existing.leadTimeDays,
          leadTimeSource: existing.leadTimeSource,
        }
      }

      const otif = supplierOtifAggregates.get(supplierKey)
      const hasOtif = Boolean(otif && otif.weightTotal > 0)
      return {
        fornecedor,
        leadTimeDays: hasOtif
          ? roundLikeExcel((otif as OtifAggregate).leadTimeWeightedTotal / (otif as OtifAggregate).weightTotal, 2)
          : options.leadTimeDays,
        leadTimeSource: hasOtif ? 'otif' : 'global',
      }
    })
    .sort((a, b) => compareText(a.fornecedor, b.fornecedor))

  const getDaysConsidered = (yearMonth: string): number => {
    const overrideDays = options.periodDaysOverride
    if (Number.isFinite(overrideDays) && (overrideDays as number) > 0)
      return overrideDays as number
    const configuredDays = options.daysConsideredByMonth?.[yearMonth]
    if (Number.isFinite(configuredDays) && (configuredDays as number) > 0)
      return configuredDays as number
    return getCalendarDays(yearMonth)
  }

  const analyticWithStock = Array.from(stockAggregates.values()).map((stock) => {
    const joinKey = makeJoinKey(stock.anoMes, stock.cnpjLoja, stock.gtin)
    const entry = entryAggregates.get(joinKey) ?? {
      comprasQtd: 0,
      comprasRs: 0,
    }
    const supplierCandidate = productSupplierCandidates.get(
      makeProductSupplierKey(stock.cnpjLoja, stock.gtin)
    )
    const fornecedor = supplierCandidate?.fornecedor ?? ''
    const leadTimeDays = options.leadTimeDays
    const flagLtDefault = 1
    const vendaRs = entry.comprasRs
    const precoMedioRs = stock.vendaQtdMes === 0 ? 0 : vendaRs / stock.vendaQtdMes
    const diasDoMes = getDaysConsidered(stock.anoMes)
    const cmd = diasDoMes === 0 ? 0 : stock.vendaQtdMes / diasDoMes
    const coberturaAtualDias = cmd === 0 ? 0 : stock.estoqueAtualQtd / cmd
    const ss = cmd * leadTimeDays
    const ddlt = cmd * leadTimeDays
    const rop = ss + ddlt
    const estoqueAlvo = cmd * options.targetCoverageDays + ddlt + ss
    const pedidoSugerido = Math.max(0, estoqueAlvo - stock.estoqueAtualQtd)
    const flagSemVenda = stock.vendaQtdMes === 0 ? 1 : 0

    const analyticRow: CompreAgoraAnalyticRow = {
      anoMes: stock.anoMes,
      cnpjLoja: stock.cnpjLoja,
      gtin: stock.gtin,
      ean: stock.gtin,
      produto: stock.produto,
      foto: stock.foto || undefined,
      fornecedor,
      vendaQtdMes: stock.vendaQtdMes,
      vendaRs,
      comprasQtd: stock.entradaQtdMes,
      comprasRs: entry.comprasRs,
      estoqueAtualQtd: stock.estoqueAtualQtd,
      precoMedioRs,
      diasDoMes,
      cmd,
      coberturaAtualDias,
      ltDias: leadTimeDays,
      flagLtDefault,
      coberturaAlvoDias: options.targetCoverageDays,
      flagCoberturaDefault: 1,
      ss,
      ddlt,
      rop,
      estoqueAlvo,
      pedidoSugerido,
      flagSemVenda,
      qtdAjustadaUsuario: null,
      pedidoFinalQtd: pedidoSugerido,
      origemPedido: 'Modelo',
      flagAjustado: 0,
      classificacao: classifyRow(
        stock.estoqueAtualQtd,
        flagSemVenda,
        ss,
        rop,
        coberturaAtualDias,
        leadTimeDays,
        options.targetCoverageDays
      ),
      chave: makeDisplayKey(stock.anoMes, stock.cnpjLoja, stock.gtin),
    }
    return {analyticRow, stock}
  })

  analyticWithStock.sort(
    (a, b) =>
      compareText(a.analyticRow.anoMes, b.analyticRow.anoMes) ||
      compareText(a.analyticRow.cnpjLoja, b.analyticRow.cnpjLoja) ||
      compareText(a.analyticRow.gtin, b.analyticRow.gtin) ||
      compareText(a.analyticRow.produto, b.analyticRow.produto)
  )

  const latestImportRows = new Map<string, CompreAgoraImportRow>()
  for (const {analyticRow, stock} of analyticWithStock) {
    latestImportRows.set(makeJoinKey('', analyticRow.cnpjLoja, analyticRow.gtin), {
      ean: analyticRow.gtin,
      produto: analyticRow.produto,
      foto: analyticRow.foto,
      cnpjLoja: analyticRow.cnpjLoja,
      fornecedor: analyticRow.fornecedor,
      saldoInicial: stock.saldoInicial,
      entrada: stock.entradaQtdMes,
      saida: analyticRow.vendaQtdMes,
      periodDays: getCalendarDays(analyticRow.anoMes),
      referenceDate: new Date(stock.lastSnapshotAt).toISOString().slice(0, 10),
      saldoFinal: stock.saldoFinal,
      estoqueAtual: analyticRow.estoqueAtualQtd,
    })
  }

  const importRows = Array.from(latestImportRows.values()).sort(
    (a, b) => compareText(a.cnpjLoja, b.cnpjLoja) || compareText(a.ean, b.ean)
  )

  return {
    matched: true,
    errors: [],
    analyticRows: analyticWithStock.map(({analyticRow}) => analyticRow),
    importRows,
    suppliers,
    roleSummaries,
    unknownFiles,
    allowedProductCount,
  }
}
